<template>
  <Disclosure
    as="nav"
    v-slot="{ open }"
    v-if="$store.state.headerType === 'NORMAL' && !hideHeader"
    class="bg-primary-500"
    data-cy="normal-header"
  >
    <div class="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
      <div class="relative flex items-center justify-between h-16">
        <div class="absolute inset-y-0 flex items-center sm:hidden">
          <!-- Mobile menu button-->
          <DisclosureButton
            class="hamburger focus-opposite"
            data-cy="hamburger-button"
            v-if="
              ((!$store.state.user && ($store.state.config.features.enableJoinFlow || $store.state.config.menu.loginPage)) ||
                natigationItems.length) &&
              !hideNavbarMenuItems
            "
          >
            <span class="sr-only">{{ $t('header.openMainMenu') }}</span>
            <MenuIcon v-if="!open" class="block h-6 w-6" aria-hidden="true" />
            <XIcon v-else class="block h-6 w-6" aria-hidden="true" />
          </DisclosureButton>
        </div>
        <div class="flex-1 flex items-center justify-center sm:items-stretch sm:justify-start h-full">
          <div class="flex-shrink-0 flex items-center ltr:pr-4 rtl:pl-4">
            <div class="cursor-pointer">
              <img class="h-8 w-auto" :src="logo" alt="Logo" data-cy="header-logo" @click="gotoHomeUrl()" />
            </div>
          </div>
          <div class="hidden sm:inline-grid sm:ms-6" data-cy="navigation-header">
            <div id="greedy-nav" class="relative flex w-full gap-x-3 overflow-x-hidden whitespace-nowrap">
              <router-link
                v-for="item in natigationItems"
                :key="item.name"
                :to="item.route"
                :data-cy="item.cy"
                class="text-opposite-500 hover:bg-primary-400 focus-visible:bg-primary-400 px-3 py-2 text-sm font-normal flex items-center rounded-md transition-colors focus-opposite"
              >
                {{ $t(item.name) }}
              </router-link>
            </div>
          </div>
        </div>
        <!-- More (...) menu -->
        <div v-if="more" id="more-menu" class="hidden sm:flex items-center w-12 z-10 h-16 hover:bg-primary-400" data-cy="more-button">
          <Menu as="div" class="block relative w-full h-full">
            <MenuButton
              class="text-opposite-500 w-full h-full rounded-md focus-ring-opposite"
              :aria-label="$t('header.moreOptions')"
              data-cy="header-more-menu-button"
            >
              <div class="inline-flex align-middle">
                <div><DotsHorizontalIcon class="h-6 w-6"></DotsHorizontalIcon></div>
              </div>
            </MenuButton>

            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems id="more-menu" class="profile-menu" data-cy="more-menu">
                <template v-for="(item, index) in natigationItems">
                  <MenuItem v-if="greedyNavigationHiddenItems.has(index)" :key="item.name" v-slot="{ active }">
                    <router-link
                      :to="item.route"
                      :data-cy="'more-' + item.cy"
                      class="focus-primary rounded-md block px-4 py-2 text-sm text-gray-700"
                      :class="{ 'bg-primary-100 text-primary-900': active }"
                      active-class="router-link-active-dropdown"
                    >
                      {{ $t(item.name) }}
                    </router-link>
                  </MenuItem>
                </template>
              </MenuItems>
            </transition>
          </Menu>
        </div>
        <!-- END More (...) menu -->
        <div class="right-menus">
          <Menu
            as="div"
            class="relative sm:block hidden h-full"
            v-if="!$store.state.user && $store.state.config.features.enableJoinFlow && !hideNavbarMenuItems"
            data-cy="navbar-join-menu"
          >
            <router-link
              to="/join"
              data-cy="navbar-join-menu-link"
              class="text-opposite-500 hover:bg-primary-400 focus-visible:bg-primary-400 px-3 py-2 text-sm font-normal flex items-center h-full rounded-md transition-colors focus-opposite"
            >
              {{ $t('header.register') }}
            </router-link>
          </Menu>
          <Menu
            as="div"
            class="relative sm:block hidden h-full"
            v-if="!$store.state.user && !hideNavbarMenuItems"
            data-cy="navbar-login-menu"
          >
            <router-link
              v-if="$store.state.config.menu.loginPage"
              to="/login"
              data-cy="navbar-login-menu-link"
              class="text-opposite-500 hover:bg-primary-400 focus-visible:bg-primary-400 px-3 py-2 text-sm font-normal flex items-center h-full rounded-md transition-colors focus-opposite"
            >
              {{ $t('header.login') }}
            </router-link>
          </Menu>
          <language-dropdown></language-dropdown>
          <!-- Profile dropdown -->
          <Menu as="div" class="ml-3 relative" v-if="$store.state.user" data-cy="profile-button">
            <div>
              <MenuButton class="menu-button focus-ring-opposite" data-cy="header-profile-menu-button">
                <span class="sr-only">{{ $t('header.openUserMenu') }}</span>
                <UserIcon class="h-6 w-6"></UserIcon>
                <div class="text-opposite-500 text-sm font-medium max-w-xl py-1.5 pr-1 hidden md:block">{{ $t('profile.myProfile') }}</div>
              </MenuButton>
            </div>

            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems class="profile-menu" data-cy="profile-menu">
                <MenuItem disabled>
                  <div class="block px-4 py-2 text-sm text-gray-700 hover:bg-transparent cursor-default select-none">
                    <p class="font-bold break-words">{{ $store.state.user.firstName }} {{ $store.state.user.lastName }}</p>
                    <p v-if="$store.state.user.accessLevel.includes('MANAGER')" class="text-green-400" data-cy="access-level-manager">
                      MANAGER
                    </p>
                    <p v-else-if="$store.state.user.accessLevel.includes('STAFF')" class="text-blue-400" data-cy="access-level-staff">
                      STAFF
                    </p>
                  </div>
                </MenuItem>
                <div class="border-b border-gray-400 mx-4"></div>
                <MenuItem v-if="$store.state.config.menu.profilePage" v-slot="{ active }">
                  <a
                    class="focus-primary rounded-md block px-4 py-2 text-sm text-gray-700 cursor-pointer"
                    :class="{ 'bg-primary-100 text-primary-900': active }"
                    active-class="router-link-active-dark"
                    data-cy="profile-link"
                    @click="routerLinkMethod('/profile')"
                  >
                    {{ $t('header.profile') }}
                  </a>
                </MenuItem>
                <MenuItem data-cy="logout-button" v-slot="{ active }">
                  <a
                    @click="signOut()"
                    class="focus-primary rounded-md block px-4 py-2 text-sm text-gray-700 cursor-pointer"
                    :class="{ 'bg-primary-100 text-primary-900': active }"
                  >
                    {{ $t('header.logout') }}
                  </a>
                </MenuItem>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>
    </div>

    <DisclosurePanel class="sm:hidden">
      <div class="px-2 pt-2 pb-3 space-y-1">
        <div v-if="!$store.state.user" class="pb-4">
          <router-link
            v-if="$store.state.config.features.enableJoinFlow"
            to="/join"
            data-cy="hamburger-join-link"
            class="hover:bg-primary-400 focus-visible:bg-primary-400 block px-3 py-2 text-base text-opposite-500 font-medium rounded-md focus-opposite"
            active-class="router-link-active-hamburger"
          >
            <DisclosureButton class="w-full text-left">{{ $t('header.register') }}</DisclosureButton>
          </router-link>
          <router-link
            v-if="$store.state.config.menu.loginPage"
            to="/login"
            data-cy="hamburger-login-link"
            class="hover:bg-primary-400 focus-visible:bg-primary-400 block px-3 py-2 text-base text-opposite-500 font-medium rounded-md focus-opposite"
            active-class="router-link-active-hamburger"
          >
            <DisclosureButton class="w-full text-left">{{ $t('header.login') }}</DisclosureButton>
          </router-link>
        </div>
        <router-link
          v-for="item in natigationItems"
          :key="item.name"
          :to="item.route"
          :data-cy="item.cy"
          class="hover:bg-primary-400 focus-visible:bg-primary-400 block px-3 py-2 text-base text-opposite-500 font-medium rounded-md focus-opposite"
          active-class="router-link-active-hamburger"
        >
          <DisclosureButton class="w-full text-left">{{ $t(item.name) }}</DisclosureButton>
        </router-link>
      </div>
    </DisclosurePanel>
  </Disclosure>

  <div v-if="$store.state.headerType === 'SMALL' && !hideHeader" class="bg-primary-500" data-cy="small-header">
    <div class="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
      <div class="relative flex items-center justify-between h-16">
        <div class="absolute inset-y-0 flex items-center sm:hidden"></div>
        <div class="flex-1 flex items-center justify-center sm:items-stretch sm:justify-start h-full">
          <div class="flex-shrink-0 flex items-center ltr:pr-4 rtl:pl-4">
            <img class="h-8 w-auto" :src="logo" alt="Logo" data-cy="small-header-logo" />
          </div>
        </div>
        <div class="right-menus">
          <language-dropdown></language-dropdown>
        </div>
      </div>
    </div>
  </div>

  <!-- DEV-52461 Need to double check what this TINY header is for -->
  <div v-if="$store.state.headerType === 'TINY' && !hideHeader" class="bg-primary-500" style="height: 0.3rem" data-cy="tiny-header"></div>

  <the-strap-line v-if="showStrapLine"></the-strap-line>

  <!-- Banner image + title(optional) -->
  <div class="relative w-full" v-if="banner && $store.state.headerType !== 'TINY'" data-cy="banner-header">
    <div class="absolute w-full bottom-0 bg-gradient-to-r rtl:bg-gradient-to-l from-primary-500 to-transparent">
      <div class="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 py-2 text-opposite-500 font-bold text-2xl">
        {{ $t(title) }}
      </div>
    </div>
    <img class="w-full h-72 md:h-80 lg:h-96 object-cover" :src="banner" alt="" />
  </div>

  <content-block v-if="$store.state.user && $store.state.headerType"></content-block>
</template>

<script lang="ts" src="./default-header.component.ts" />

<style lang="less" scoped>
@tailwind components;
@layer components {
  .hamburger {
    @apply inline-flex items-center justify-center p-2 rounded-md text-opposite-500 hover:text-opposite-500 hover:bg-primary-700;
  }
  .profile-menu {
    @apply absolute ltr:right-0 rtl:left-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none;
  }
  .router-link-active {
    @apply font-bold;
  }
  .router-link-active-dropdown {
    @apply font-bold;
  }
  .router-link-active-hamburger > * {
    @apply font-bold;
  }
  .menu-button {
    @apply flex text-sm rounded-full text-opposite-500 hover:bg-primary-700 px-2 py-1 items-center;
  }
  .right-menus {
    @apply absolute inset-y-0 ltr:right-0 rtl:left-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pl-6 sm:pr-0 z-50 h-full;
  }
}
</style>
